// web-frontend/src/shared/stores/chatsStore.js - MICE 사업 최적화 채팅 스토어 (NET ZERO SAPIENS)
// 🔥🔥🔥 2025-12-20 수정사항:
// 1. fetchChatRooms 에러 핸들링 개선 - 에러 시 기존 데이터 유지 (백엔드 재시작 대응)
// 2. WebSocket 재연결 시 채팅방 목록 자동 갱신
// 3. handleNewMessage: 미팅 메시지 미리보기에서 보낸사람/받는사람 구분
// 4. fetchChatRooms: last_message 정규화 시 sender_id 고려하여 미리보기 텍스트 생성
// 5. generateMeetingPreviewText 헬퍼 함수 추가
// 🔥🔥🔥 2025-12-25 수정사항:
// 1. createOrGetCompanyChatRoom: 새 채팅방 생성 시 chatRooms 배열에도 추가 (채팅방 목록 동기화)
// 2. createOrGetCompanyChatRoom: 기존 채팅방 발견 시 participants 정보 보완 및 chatRooms 업데이트 강화
// 🔥🔥🔥 2026-01-07 수정사항:
// 1. handleUserLeftChat: 상대방 채팅방 퇴장 처리 핸들러 추가
import { create } from 'zustand';
import chatsService from '../services/chatsService';
import i18nService from '../services/i18n';
import { getStoredUserId, DEV_USER_ID, isValidUUID } from '../utils/storage';
import logger from '../utils/logger';
// 번역 API 서비스 (Google Translate API 또는 다른 서비스로 교체 가능)
const translationService = {
  async translate(text, targetLang, sourceLang = 'auto') {
    try {
      const response = await fetch('/api/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, target: targetLang, source: sourceLang })
      });
      const data = await response.json();
      return data.translatedText || text;
    } catch (error) {
      logger.error('Translation failed:', error);
      return text;
    }
  },
  
  supportedLanguages: [
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  ]
};
export const MEETING_MESSAGE_TYPES = [
  'meeting_proposal',
  'meeting_request',
  'meeting_reschedule',
  'meeting_rescheduled',
  'meeting_cancelled',
  'schedule',
  'meeting_accepted',
  'meeting_rejected',
  'meeting_response',
];
const t = (key, fallback, params = {}) => {
  const value = i18nService.t(key, params);
  return value && value !== key ? value : fallback;
};
// 🔥🔥🔥 [추가] 미팅 메시지 미리보기 텍스트 생성 헬퍼 함수
// [2026-04-28] export 추가: MainLayout 의 글로벌 미팅 토스트에서 동일 로직 재사용.
export const generateMeetingPreviewText = (msgType, isSentByMe, metadata = {}) => {
  const eventKey = metadata?.event_key || metadata?.meeting_status_event;
  const proposalIntent = metadata?.proposal_intent;

  // 미팅 제안
  if (['meeting_proposal', 'meeting_request', 'meeting_reschedule', 'schedule'].includes(msgType)) {
    if (msgType === 'meeting_reschedule' || proposalIntent === 'reschedule') {
      return isSentByMe
        ? t('chat.preview_meeting_reschedule_sent', '🕒 You sent a reschedule proposal.')
        : t('chat.preview_meeting_reschedule_received', '🕒 Reschedule proposal received.');
    }
    return isSentByMe 
      ? t('chat.preview_meeting_sent', '📅 You sent a meeting proposal.')
      : t('chat.preview_meeting_received', '📅 Meeting proposal received.');
  }
  
  // 미팅 수락
  if (msgType === 'meeting_accepted') {
    return isSentByMe 
      ? t('chat.preview_meeting_accepted_sent', '✅ You accepted the meeting.')
      : t('chat.preview_meeting_accepted_received', '✅ Meeting was accepted.');
  }
  
  // 미팅 거절
  if (msgType === 'meeting_rejected') {
    return isSentByMe 
      ? t('chat.preview_meeting_rejected_sent', '❌ You declined the meeting.')
      : t('chat.preview_meeting_rejected_received', '❌ Meeting was declined.');
  }

  if (msgType === 'meeting_cancelled' || eventKey === 'meeting_cancelled') {
    return isSentByMe
      ? t('chat.preview_meeting_cancelled_sent', '🚫 You cancelled the meeting.')
      : t('chat.preview_meeting_cancelled_received', '🚫 Meeting was cancelled.');
  }

  if (msgType === 'meeting_rescheduled' || eventKey === 'meeting_rescheduled') {
    return isSentByMe
      ? t('chat.preview_meeting_rescheduled_sent', '🕒 You accepted the reschedule.')
      : t('chat.preview_meeting_rescheduled_received', '🕒 Meeting was rescheduled.');
  }
  
  // 미팅 응답 (metadata.response로 수락/거절 구분)
  if (msgType === 'meeting_response') {
    const responseAction = metadata?.response;
    if (eventKey === 'meeting_rescheduled') {
      return isSentByMe
        ? t('chat.preview_meeting_rescheduled_sent', '🕒 You accepted the reschedule.')
        : t('chat.preview_meeting_rescheduled_received', '🕒 Meeting was rescheduled.');
    }
    if (eventKey === 'meeting_cancelled' || responseAction === 'cancel') {
      return isSentByMe
        ? t('chat.preview_meeting_cancelled_sent', '🚫 You cancelled the meeting.')
        : t('chat.preview_meeting_cancelled_received', '🚫 Meeting was cancelled.');
    }
    if (responseAction === 'accept') {
      return isSentByMe 
        ? t('chat.preview_meeting_accepted_sent', '✅ You accepted the meeting.')
        : t('chat.preview_meeting_accepted_received', '✅ Meeting was accepted.');
    } else {
      return isSentByMe 
        ? t('chat.preview_meeting_rejected_sent', '❌ You declined the meeting.')
        : t('chat.preview_meeting_rejected_received', '❌ Meeting was declined.');
    }
  }
  
  // 기본값
  return isSentByMe 
    ? t('chat.preview_meeting_sent', '📅 You sent a meeting proposal.')
    : t('chat.preview_meeting_received', '📅 Meeting proposal received.');
};
const useChatsStore = create((set, get) => ({
  // =================== 기본 상태 ===================
  chatRooms: [],
  selectedChatRoom: null,
  messages: {}, // { [chatRoomId]: messages[] }
  currentUserId: null,
  
  // =================== MICE 행사 관련 상태 ===================
  eventChatRooms: {},
  eventSchedules: {},
  participantLimit: {},
  
  // =================== 번역 관련 상태 ===================
  userLanguagePreferences: {},
  translatedMessages: {},
  autoTranslateEnabled: true,
  preferredLanguage: 'ko',
  
  // =================== 실시간 상태 ===================
  onlineUsers: new Set(),
  typingUsers: {}, // { [chatRoomId]: userId[] }
  activeEvents: [],
  
  // =================== UI 상태 ===================
  isLoading: false,
  isLoadingMessages: false,
  isLoadingMore: false,
  isSending: false,
  isTranslating: false,
  error: null,
  
  // 🔥🔥🔥 [추가] 네트워크 상태 추적
  isNetworkError: false,
  lastFetchAttempt: null,
  retryCount: 0,
  
  // =================== 페이지네이션 ===================
  messagePages: {},
  hasMoreMessages: {},
  
  // =================== 읽음 상태 ===================
  unreadCounts: {},
  lastReadMessageIds: {},
  
  // =================== 검색 ===================
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  // =================== 현재 사용자 설정 ===================
  
  setCurrentUserId: (userId) => {
    logger.debug('✅ 현재 사용자 ID 설정:', userId);
    set({ currentUserId: userId });
  },
  // =================== 온라인 상태 관리 ===================
  
  updateUserOnlineStatus: (userId, isOnline) => {
    if (!userId) {
      logger.warn('⚠️ updateUserOnlineStatus: userId가 없습니다.');
      return;
    }
    
    set(state => {
      const newOnlineUsers = new Set(state.onlineUsers);
      
      if (isOnline) {
        newOnlineUsers.add(userId);
        logger.debug(`✅ 사용자 온라인: ${userId}`);
      } else {
        newOnlineUsers.delete(userId);
        logger.debug(`⭕ 사용자 오프라인: ${userId}`);
      }
      
      const updatedChats = state.chatRooms.map(chat => {
        if (chat.participants?.some(p => p.user_id === userId || p.id === userId)) {
          return {
            ...chat,
            participants: chat.participants.map(p => {
              const participantId = p.user_id || p.id;
              return participantId === userId 
                ? { 
                    ...p, 
                    is_online: isOnline, 
                    last_seen: isOnline ? null : new Date().toISOString() 
                  }
                : p;
            })
          };
        }
        return chat;
      });
      
      return { 
        onlineUsers: newOnlineUsers,
        chatRooms: updatedChats
      };
    });
  },
  
  handleUserOnline: (userId) => {
    get().updateUserOnlineStatus(userId, true);
  },
  
  handleUserOffline: (userId) => {
    get().updateUserOnlineStatus(userId, false);
  },
  
  isUserOnline: (userId) => {
    if (!userId) return false;
    return get().onlineUsers.has(userId);
  },
  
  getOnlineCount: (chatId) => {
    const chat = get().chatRooms.find(c => c && c.id === chatId);
    if (!chat?.participants) return 0;
    
    return chat.participants.filter(p => {
      const participantId = p.user_id || p.id;
      return get().onlineUsers.has(participantId);
    }).length;
  },
  
  requestOnlineStatuses: async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    
    try {
      logger.debug('🔍 온라인 상태 조회 요청:', userIds.length);
      const response = await chatsService.getOnlineStatuses(userIds);
      
      if (response && Array.isArray(response)) {
        response.forEach(({ userId, isOnline }) => {
          get().updateUserOnlineStatus(userId, isOnline);
        });
        logger.debug('✅ 온라인 상태 로드 완료');
      }
    } catch (error) {
      logger.error('❌ 온라인 상태 조회 실패:', error);
    }
  },
  // =================== 🔥 미읽은 메시지 관리 (강화) ===================
  
  updateUnreadCount: (chatId, count) => {
    if (!chatId) {
      logger.warn('⚠️ updateUnreadCount: chatId가 없습니다.');
      return;
    }
    
    const validCount = Math.max(0, parseInt(count, 10) || 0);
    
    logger.debug(`📬 [updateUnreadCount] chatId=${chatId}, count=${count} → validCount=${validCount}`);
    
    set(state => {
      const newUnreadCounts = {
        ...state.unreadCounts,
        [chatId]: validCount
      };
      
      const updatedChats = state.chatRooms.map(chat => 
        chat.id === chatId 
          ? { 
              ...chat, 
              unread_count: validCount,
              unreadCount: validCount,
              has_unread: validCount > 0
            }
          : chat
      );
      
      logger.debug(`✅ [updateUnreadCount] 상태 업데이트 완료 - chatId=${chatId}, unread=${validCount}`);
      
      return {
        unreadCounts: newUnreadCounts,
        chatRooms: updatedChats
      };
    });
    
    const totalUnread = get().getTotalUnreadCount();
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('unreadCountChanged', { 
        detail: { 
          total: totalUnread, 
          chatId, 
          count: validCount 
        } 
      }));
      logger.debug(`📢 [이벤트 발생] unreadCountChanged - total=${totalUnread}, chatId=${chatId}, count=${validCount}`);
    }
  },
  
  incrementUnreadCount: (chatId) => {
    const currentCount = get().unreadCounts[chatId] || 0;
    logger.debug(`📬 [incrementUnreadCount] chatId=${chatId}, currentCount=${currentCount} → ${currentCount + 1}`);
    get().updateUnreadCount(chatId, currentCount + 1);
  },
  
  getTotalUnreadCount: () => {
    const { unreadCounts } = get();
    const total = Object.values(unreadCounts).reduce((sum, count) => sum + (count || 0), 0);
    logger.debug(`📊 [getTotalUnreadCount] 전체 미읽은 메시지: ${total}개`);
    return total;
  },
  
  get totalUnreadCount() {
    return get().getTotalUnreadCount();
  },
  // =================== 미팅 연동 헬퍼 ===================
  
  hasMeetingProposal: (messageId) => {
    const messages = Object.values(get().messages).flat();
    const message = messages.find(m => m.id === messageId);
    return message?.message_type === 'meeting_proposal' || 
           message?.message_type === 'meeting_request';
  },
  
  getMeetingFromMessage: (messageId) => {
    const messages = Object.values(get().messages).flat();
    const message = messages.find(m => m.id === messageId);
    const metadata = message?.metadata || message?.message_metadata;
    return metadata?.meeting || null;
  },
  
  updateMeetingMessageStatus: (messageId, status) => {
    set(state => {
      const updatedMessages = {};
      
      Object.keys(state.messages).forEach(chatId => {
        updatedMessages[chatId] = state.messages[chatId].map(msg => 
          msg.id === messageId
            ? {
                ...msg,
                metadata: {
                  ...(msg.metadata || msg.message_metadata || {}),
                  invitation_status: status,
                  updated_at: new Date().toISOString()
                }
              }
            : msg
        );
      });
      
      return { messages: updatedMessages };
    });
    
    logger.debug(`✅ 미팅 메시지 상태 업데이트: ${messageId} -> ${status}`);
  },
  // =================== MICE 행사 관련 액션 ===================
  
  createEventChatRoom: async (eventData) => {
    const {
      eventId,
      eventName,
      eventType,
      startDate,
      endDate,
      maxParticipants = 500,
      languages = ['ko', 'en'],
      organizers = [],
      description = ''
    } = eventData;
    set({ isLoading: true, error: null });
    try {
      logger.debug('🎪 MICE 행사 채팅방 생성:', eventName);
      
      const chatRoomData = {
        room_name: `📅 ${eventName}`,
        room_type: 'event',
        event_id: eventId,
        event_type: eventType,
        max_participants: maxParticipants,
        supported_languages: languages,
        metadata: {
          eventType,
          startDate,
          endDate,
          description,
          organizers,
          autoTranslate: true,
          participantCount: 0
        }
      };
      const chatRoom = await chatsService.createChatRoom(chatRoomData);
      
      const scheduleMessage = {
        type: 'event_schedule',
        content: `${eventName} 행사가 생성되었습니다.`,
        metadata: {
          startDate,
          endDate,
          eventType,
          description
        }
      };
      
      await get().sendMessage(chatRoom.id, scheduleMessage);
      
      const welcomeMessages = {
        ko: `안녕하세요! ${eventName}에 오신 것을 환영합니다! 🎉`,
        en: `Welcome to ${eventName}! 🎉`,
        zh: `欢迎来到 ${eventName}! 🎉`,
        ja: `${eventName}へようこそ! 🎉`
      };
      
      for (const [lang, message] of Object.entries(welcomeMessages)) {
        if (languages.includes(lang)) {
          await get().sendSystemMessage(chatRoom.id, message, lang);
        }
      }
      
      set(state => ({
        chatRooms: [chatRoom, ...state.chatRooms],
        eventChatRooms: {
          ...state.eventChatRooms,
          [eventId]: chatRoom.id
        },
        participantLimit: {
          ...state.participantLimit,
          [chatRoom.id]: maxParticipants
        },
        activeEvents: [...state.activeEvents, eventData],
        selectedChatRoom: chatRoom,
        isLoading: false,
        error: null
      }));
      logger.debug('✅ MICE 행사 채팅방 생성 완료:', chatRoom.id);
      return chatRoom;
    } catch (error) {
      logger.error('❌ MICE 행사 채팅방 생성 실패:', error);
      set({
        error: error.message || '행사 채팅방 생성 실패',
        isLoading: false
      });
      throw error;
    }
  },
  manageLargeGroupChat: async (chatRoomId, action, data = {}) => {
    try {
      switch (action) {
        case 'split_breakout':
          const { groupSize = 10, topic } = data;
          const participants = await chatsService.getChatParticipants(chatRoomId);
          const groups = [];
          
          for (let i = 0; i < participants.length; i += groupSize) {
            const groupParticipants = participants.slice(i, i + groupSize);
            const breakoutRoom = await get().createChatRoom(
              groupParticipants.map(p => p.id),
              'breakout',
              `${topic} - Group ${Math.floor(i / groupSize) + 1}`
            );
            groups.push(breakoutRoom);
          }
          
          return groups;
          
        case 'broadcast':
          const { message, languages = ['ko', 'en'] } = data;
          const translatedMessages = {};
          
          for (const lang of languages) {
            if (lang !== 'ko') {
              translatedMessages[lang] = await translationService.translate(message, lang, 'ko');
            } else {
              translatedMessages[lang] = message;
            }
          }
          
          for (const [lang, translatedMessage] of Object.entries(translatedMessages)) {
            await get().sendSystemMessage(chatRoomId, translatedMessage, lang, 'broadcast');
          }
          
          return translatedMessages;
          
        case 'participant_limit':
          const { maxParticipants } = data;
          set(state => ({
            participantLimit: {
              ...state.participantLimit,
              [chatRoomId]: maxParticipants
            }
          }));
          
          await chatsService.updateChatRoom(chatRoomId, { max_participants: maxParticipants });
          return { success: true, maxParticipants };
          
        default:
          throw new Error(`Unknown action: ${action}`);
      }
    } catch (error) {
      logger.error(`❌ 대규모 그룹 채팅 관리 실패 (${action}):`, error);
      throw error;
    }
  },
  // =================== 실시간 번역 기능 ===================
  
  translateMessage: async (messageId, targetLanguage, chatRoomId) => {
    const messages = get().messages[chatRoomId];
    const message = messages?.find(m => m.id === messageId);
    
    if (!message) {
      logger.warn('번역할 메시지를 찾을 수 없음:', messageId);
      return null;
    }
    
    const existingTranslation = get().translatedMessages[messageId]?.[targetLanguage];
    if (existingTranslation) {
      return existingTranslation;
    }
    
    set({ isTranslating: true });
    
    try {
      const translatedText = await translationService.translate(
        message.content,
        targetLanguage,
        message.language || 'auto'
      );
      
      set(state => ({
        translatedMessages: {
          ...state.translatedMessages,
          [messageId]: {
            ...state.translatedMessages[messageId],
            [targetLanguage]: translatedText
          }
        },
        isTranslating: false
      }));
      
      return translatedText;
    } catch (error) {
      logger.error('❌ 메시지 번역 실패:', error);
      set({ isTranslating: false });
      return message.content;
    }
  },
  translateChatRoom: async (chatRoomId, targetLanguage) => {
    const messages = get().messages[chatRoomId] || [];
    const translationPromises = [];
    
    set({ isTranslating: true });
    
    for (const message of messages) {
      if (message.type === 'text' && !get().translatedMessages[message.id]?.[targetLanguage]) {
        translationPromises.push(
          get().translateMessage(message.id, targetLanguage, chatRoomId)
        );
      }
    }
    
    try {
      await Promise.all(translationPromises);
      logger.debug(`✅ 채팅방 전체 번역 완료: ${targetLanguage}`);
      set({ isTranslating: false });
    } catch (error) {
      logger.error('❌ 채팅방 번역 실패:', error);
      set({ isTranslating: false });
    }
  },
  setUserLanguage: (language) => {
    set({ preferredLanguage: language });
    
    const { selectedChatRoom, autoTranslateEnabled } = get();
    if (selectedChatRoom && autoTranslateEnabled) {
      get().translateChatRoom(selectedChatRoom.id, language);
    }
  },
  toggleAutoTranslate: () => {
    set(state => ({ autoTranslateEnabled: !state.autoTranslateEnabled }));
  },
  // =================== 일정 공유 기능 ===================
  
  sendScheduleMessage: async (chatRoomId, scheduleData) => {
    const {
      title,
      startTime,
      endTime,
      location,
      description,
      attendees = [],
      reminder = true
    } = scheduleData;
    try {
      const scheduleMessage = {
        type: 'schedule',
        content: title,
        metadata: {
          startTime,
          endTime,
          location,
          description,
          attendees,
          reminder,
          createdBy: get().currentUserId || 'current-user'
        }
      };
      const message = await get().sendMessage(chatRoomId, scheduleMessage);
      
      set(state => ({
        eventSchedules: {
          ...state.eventSchedules,
          [chatRoomId]: [
            ...(state.eventSchedules[chatRoomId] || []),
            {
              id: message.id,
              ...scheduleData,
              chatRoomId
            }
          ]
        }
      }));
      if (attendees.length > 0) {
        await get().notifyAttendees(chatRoomId, attendees, scheduleData);
      }
      return message;
    } catch (error) {
      logger.error('❌ 일정 전송 실패:', error);
      throw error;
    }
  },
  syncWithCalendar: async (chatRoomId, calendarType = 'google') => {
    try {
      const schedules = get().eventSchedules[chatRoomId] || [];
      
      const calendarEvents = schedules.map(schedule => ({
        summary: schedule.title,
        start: { dateTime: schedule.startTime },
        end: { dateTime: schedule.endTime },
        location: schedule.location,
        description: schedule.description,
        attendees: schedule.attendees.map(a => ({ email: a.email }))
      }));
      logger.debug(`✅ ${schedules.length}개 일정이 ${calendarType} 캘린더와 동기화됨`);
      return { success: true, syncedCount: schedules.length };
      
    } catch (error) {
      logger.error('❌ 캘린더 동기화 실패:', error);
      throw error;
    }
  },
  notifyAttendees: async (chatRoomId, attendees, scheduleData) => {
    const { title, startTime, location } = scheduleData;
    
    for (const attendee of attendees) {
      const userLang = get().userLanguagePreferences[attendee.id] || 'ko';
      let notificationMessage = `📅 일정 알림: ${title}\n시간: ${startTime}\n장소: ${location}`;
      
      if (userLang !== 'ko') {
        notificationMessage = await translationService.translate(notificationMessage, userLang, 'ko');
      }
    }
  },
  // =================== 🔥🔥🔥 채팅방 목록 조회 (에러 핸들링 강화 - 백엔드 재시작 대응) ===================
  
  fetchChatRooms: async (refresh = false) => {
    // 🔥🔥🔥 [추가] 이미 로딩 중이면 중복 호출 방지
    if (get().isLoading && !refresh) {
      logger.debug('⏳ 이미 채팅방 목록 조회 중...');
      return get().chatRooms;
    }
    
    set({ isLoading: true, error: null, lastFetchAttempt: new Date().toISOString() });
    
    try {
      logger.debug('📋 채팅방 목록 조회 시작...');
      const response = await chatsService.getChatRooms();
      
      let chatRoomsData = [];
      
      if (response) {
        if (Array.isArray(response)) {
          chatRoomsData = response;
        } else if (response.chats && Array.isArray(response.chats)) {
          chatRoomsData = response.chats;
        } else if (response.data) {
          if (Array.isArray(response.data)) {
            chatRoomsData = response.data;
          } else if (response.data.chats && Array.isArray(response.data.chats)) {
            chatRoomsData = response.data.chats;
          }
        }
      }
      
      const activeChatRooms = chatRoomsData;
      
      logger.debug(`📊 필터링 결과: 전체 ${chatRoomsData.length}개 → 활성 ${activeChatRooms.length}개`);
      
      // 중복 채팅방 제거
      const uniqueChatRooms = [];
      const participantKeysMap = new Map();
      
      activeChatRooms.forEach(room => {
        const roomType = room.room_type || room.chat_type || '';
        const isDirectChat = roomType === 'direct' || roomType === 'one_to_one';
        
        if (!isDirectChat) {
          uniqueChatRooms.push(room);
          return;
        }
        
        const participants = Array.isArray(room.participants) ? room.participants : [];
        const participantIds = participants
          .map(p => String(p.id || p.user_id || p.participant_id))
          .filter(id => id != null)
          .sort()
          .join('-');
        
        if (!participantIds) {
          uniqueChatRooms.push(room);
          return;
        }
        
        if (participantKeysMap.has(participantIds)) {
          const existingRoom = participantKeysMap.get(participantIds);
          
          const existingDate = new Date(existingRoom.last_activity || existingRoom.updated_at || existingRoom.created_at || 0);
          const newDate = new Date(room.last_activity || room.updated_at || room.created_at || 0);
          
          if (newDate > existingDate) {
            participantKeysMap.set(participantIds, room);
            const index = uniqueChatRooms.findIndex(r => r.id === existingRoom.id);
            if (index !== -1) {
              uniqueChatRooms[index] = room;
            }
            logger.debug(`🔄 중복 채팅방 교체: ${existingRoom.id} → ${room.id}`);
          } else {
            logger.debug(`⚠️ 중복 채팅방 제외: ${room.id} (기존: ${existingRoom.id})`);
          }
        } else {
          participantKeysMap.set(participantIds, room);
          uniqueChatRooms.push(room);
        }
      });
      
      logger.debug(`🧹 중복 제거: ${activeChatRooms.length}개 → ${uniqueChatRooms.length}개`);
      // 🔥🔥🔥 [핵심 수정] 현재 사용자 ID 가져오기
      const currentUserId = get().currentUserId;
      
      // 🔥🔥🔥 [핵심 수정] last_message 정규화 추가 - 보낸사람/받는사람 구분
      const normalizedChatRooms = uniqueChatRooms.map(room => {
        // 기존 로컬 상태에서 해당 채팅방 찾기
        const existingRoom = get().chatRooms.find(r => r.id === room.id);
        
        let normalizedLastMessage = '';
        let normalizedLastMessageObj = null;
        
        const backendLastMessage = room.last_message;
        const backendLastMessageObj = room.lastMessage;
        
        // 타입별 처리
        if (typeof backendLastMessage === 'string' && backendLastMessage.trim()) {
          normalizedLastMessage = backendLastMessage;
        } else if (typeof backendLastMessage === 'object' && backendLastMessage !== null) {
          const msgType = backendLastMessage.message_type || backendLastMessage.type || 'text';
          const content = backendLastMessage.content || '';
          const senderId = backendLastMessage.sender_id;
          const metadata = backendLastMessage.metadata || backendLastMessage.message_metadata || {};
          
          // 🔥🔥🔥 [핵심] 내가 보낸 메시지인지 확인
          const isSentByMe = currentUserId && String(senderId) === String(currentUserId);
          
          if (MEETING_MESSAGE_TYPES.includes(msgType)) {
            normalizedLastMessage = generateMeetingPreviewText(msgType, isSentByMe, metadata);
          } else if (msgType === 'image') {
            normalizedLastMessage = '📷 사진';
          } else if (msgType === 'file') {
            normalizedLastMessage = '📎 파일';
          } else {
            normalizedLastMessage = content || '새 메시지';
          }
          normalizedLastMessageObj = backendLastMessage;
        } else if (backendLastMessageObj && typeof backendLastMessageObj === 'object') {
          const msgType = backendLastMessageObj.message_type || backendLastMessageObj.type || 'text';
          const content = backendLastMessageObj.content || '';
          const senderId = backendLastMessageObj.sender_id;
          const metadata = backendLastMessageObj.metadata || backendLastMessageObj.message_metadata || {};
          
          // 🔥🔥🔥 [핵심] 내가 보낸 메시지인지 확인
          const isSentByMe = currentUserId && String(senderId) === String(currentUserId);
          
          if (MEETING_MESSAGE_TYPES.includes(msgType)) {
            normalizedLastMessage = generateMeetingPreviewText(msgType, isSentByMe, metadata);
          } else if (msgType === 'image') {
            normalizedLastMessage = '📷 사진';
          } else if (msgType === 'file') {
            normalizedLastMessage = '📎 파일';
          } else {
            normalizedLastMessage = content || '새 메시지';
          }
          normalizedLastMessageObj = backendLastMessageObj;
        }
        
        // 로컬 상태에 이미 값이 있으면 우선 사용
        if (existingRoom) {
          if (!normalizedLastMessage && existingRoom.last_message) {
            normalizedLastMessage = existingRoom.last_message;
          }
          if (!normalizedLastMessageObj && existingRoom.lastMessage) {
            normalizedLastMessageObj = existingRoom.lastMessage;
          }
        }
        
        return {
          ...room,
          last_message: normalizedLastMessage || '',
          lastMessage: normalizedLastMessageObj || null,
        };
      });
      const eventRooms = [];
      const regularRooms = [];
      
      normalizedChatRooms.forEach(room => {
        if (room.room_type === 'event' || room.event_id) {
          eventRooms.push(room);
        } else {
          regularRooms.push(room);
        }
      });
      
      // 🔥 백엔드에서 제공하는 unread_count 추출
      const unreadCounts = {};
      normalizedChatRooms.forEach(room => {
        if (room && room.id) {
          const backendUnreadCount = 
            room.unread_count ??
            room.unreadCount ??
            0;
          
          const validatedCount = Math.max(0, parseInt(backendUnreadCount, 10) || 0);
          
          unreadCounts[room.id] = validatedCount;
          
          logger.debug(
            `📬 [fetchChatRooms] chatId=${room.id}, ` +
            `backend_unread=${backendUnreadCount}, ` +
            `validated=${validatedCount}`
          );
        }
      });
      
      // 시간순 정렬
      const sortedChatRooms = normalizedChatRooms.sort((a, b) => {
        if (a.room_type === 'event' && b.room_type !== 'event') return -1;
        if (a.room_type !== 'event' && b.room_type === 'event') return 1;
        
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        
        const dateA = new Date(a.last_activity || a.lastActivity || a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0);
        const dateB = new Date(b.last_activity || b.lastActivity || b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0);
        return dateB - dateA;
      });
      set({
        chatRooms: sortedChatRooms,
        unreadCounts,
        isLoading: false,
        error: null,
        // 🔥🔥🔥 [추가] 네트워크 에러 상태 초기화
        isNetworkError: false,
        retryCount: 0,
      });
      const totalUnread = get().getTotalUnreadCount();
      logger.debug(
        `✅ 채팅방 조회 완료: 총 ${sortedChatRooms.length}개 ` +
        `(행사: ${eventRooms.length}, 일반: ${regularRooms.length}), ` +
        `전체 미읽음: ${totalUnread}개`
      );
      
      // 참가자들의 온라인 상태 요청
      const allParticipantIds = new Set();
      sortedChatRooms.forEach(room => {
        if (room.participants) {
          room.participants.forEach(p => {
            const participantId = p.user_id || p.id;
            if (participantId) allParticipantIds.add(participantId);
          });
        }
      });
      
      if (allParticipantIds.size > 0) {
        get().requestOnlineStatuses(Array.from(allParticipantIds));
      }
      return sortedChatRooms;
    } catch (error) {
      logger.error('❌ 채팅방 목록 조회 실패:', error);
      
      // 🔥🔥🔥 [핵심 수정] 에러 발생 시 기존 데이터 유지 - 백엔드 재시작 시 채팅방 목록 사라지는 문제 해결
      const existingRooms = get().chatRooms;
      const hasExistingData = Array.isArray(existingRooms) && existingRooms.length > 0;
      
      // 네트워크 에러인지 확인
      const isNetworkError = 
        error.message?.includes('Network') || 
        error.message?.includes('fetch') ||
        error.message?.includes('Failed to fetch') ||
        error.code === 'NETWORK_ERROR' ||
        !navigator.onLine;
      
      logger.debug(`🔍 에러 분석: isNetworkError=${isNetworkError}, hasExistingData=${hasExistingData}`);
      
      set(state => ({
        // 🔥🔥🔥 [핵심] 기존 데이터가 있으면 유지, 없으면 빈 배열
        // chatRooms: [],  // ❌ 이전 코드 - 삭제하지 않음!
        chatRooms: hasExistingData ? existingRooms : [],  // ✅ 기존 데이터 유지
        error: error.message || '채팅방 목록 조회 실패',
        isLoading: false,
        // 🔥🔥🔥 [추가] 네트워크 에러 상태 추적
        isNetworkError: isNetworkError,
        retryCount: state.retryCount + 1,
      }));
      
      // 🔥🔥🔥 [추가] 네트워크 에러 시 자동 재시도 (최대 3회)
      const currentRetryCount = get().retryCount;
      if (isNetworkError && currentRetryCount < 3) {
        const retryDelay = Math.min(1000 * Math.pow(2, currentRetryCount), 10000); // 1s, 2s, 4s... 최대 10s
        logger.debug(`🔄 ${retryDelay}ms 후 자동 재시도... (${currentRetryCount + 1}/3)`);
        
        setTimeout(() => {
          logger.debug(`🔄 채팅방 목록 자동 재조회 시도 (${currentRetryCount + 1}/3)`);
          get().fetchChatRooms(true);
        }, retryDelay);
      }
      
      // 기존 데이터가 있으면 반환, 없으면 빈 배열
      return hasExistingData ? existingRooms : [];
    }
  },
  // 🔥🔥🔥 [추가] WebSocket 재연결 시 호출되는 핸들러
  handleWebSocketReconnected: () => {
    logger.debug('🔄 [Store] WebSocket 재연결 감지 - 채팅방 목록 갱신');
    
    // 약간의 딜레이 후 채팅방 목록 갱신 (백엔드가 완전히 준비될 시간 확보)
    setTimeout(() => {
      get().fetchChatRooms(true);
    }, 1000);
  },
  // 🔥🔥🔥 2025-12-25 수정: createOrGetCompanyChatRoom 함수 개선
  // - 수정 1: 새 채팅방 생성 시 chatRooms 배열에도 추가
  // - 수정 2: 기존 채팅방 발견 시 participants 정보 보완 및 chatRooms 업데이트 강화
  createOrGetCompanyChatRoom: async (companyId, companyName, currentUserId, options = {}) => {
    logger.debug('🔥 createOrGetCompanyChatRoom 시작:', { companyId, companyName, currentUserId });
    
    const {
      sendWelcomeMessage = false,
      welcomeMessageText = null,
    } = options;
    
    let validUserId = currentUserId;
    
    try {
      if (!isValidUUID(currentUserId)) {
        logger.warn('⚠️ 제공된 사용자 ID가 UUID 형식이 아님:', currentUserId);
        const storedId = await getStoredUserId();
        validUserId = storedId || DEV_USER_ID;
        logger.debug('✅ 저장된 사용자 ID 사용:', validUserId);
      }
      
      const storedId = await getStoredUserId();
      if (storedId && storedId !== validUserId) {
        logger.debug('🔄 사용자 ID 교체:', validUserId, '->', storedId);
        validUserId = storedId;
      }
    } catch (error) {
      logger.error('❌ 사용자 ID 검증 실패:', error);
      validUserId = DEV_USER_ID;
    }
    
    logger.debug('✅ 최종 사용자 ID:', validUserId);
    
    if (!companyId || !companyName || !validUserId) {
      logger.error('❌ 필수 파라미터 누락:', { companyId, companyName, validUserId });
      throw new Error('필수 정보가 누락되었습니다.');
    }
    
    set({ isLoading: true, error: null });
    
    try {
      logger.debug('🔍 1단계: 서버 API로 기존 채팅방 검색...');
      
      const participantIds = [validUserId, companyId];
      const existingChat = await chatsService.findChatByParticipants(participantIds);
      
      // 🔥🔥🔥 [수정 2] 기존 채팅방 발견 시 participants 정보 보완 및 chatRooms 동기화 강화
      if (existingChat && existingChat.id) {
        logger.debug('✅ 서버에서 기존 채팅방 발견:', existingChat.id);
        
        // 🔥🔥🔥 [수정] participants 정보가 없으면 보완
        if (!existingChat.participants || existingChat.participants.length === 0) {
          existingChat.participants = [
            { 
              id: validUserId, 
              user_id: validUserId,
              name: 'You',
              user_name: 'You',
              role: 'admin',
              is_online: true
            },
            { 
              id: companyId, 
              user_id: companyId,
              name: companyName,
              user_name: companyName,
              role: 'member',
              is_online: false
            }
          ];
        }
        
        set(state => {
          const currentRooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
          const exists = currentRooms.some(room => room && room.id === existingChat.id);
          
          // 🔥 기존 채팅방 정보 업데이트 (participants 등)
          const updatedRooms = exists 
            ? currentRooms.map(room => room.id === existingChat.id ? { ...room, ...existingChat } : room)
            : [existingChat, ...currentRooms];
          
          return {
            selectedChatRoom: existingChat,
            chatRooms: updatedRooms,
            isLoading: false,
            error: null
          };
        });
        
        return existingChat;
      }
      
      logger.debug('ℹ️ 기존 채팅방이 없습니다. 새로 생성합니다...');
      
      const chatRoomData = {
        participant_ids: [validUserId, companyId],
        room_name: companyName,
        room_type: 'direct',
        metadata: {
          companyId: companyId,
          companyName: companyName,
          createdBy: validUserId,
          createdFor: 'business_inquiry',
          createdAt: new Date().toISOString()
        }
      };
      
      logger.debug('📤 채팅방 생성 요청:', chatRoomData);
      
      const newChatRoom = await chatsService.createChatRoom(chatRoomData);
      
      if (!newChatRoom || !newChatRoom.id) {
        throw new Error('채팅방 생성에 실패했습니다.');
      }
      
      logger.debug('✅ 새 채팅방 생성 완료:', newChatRoom.id);
      
      if (!newChatRoom.participants || newChatRoom.participants.length === 0) {
        logger.warn('⚠️ participants 없음. 기본값 설정...');
        newChatRoom.participants = [
          { 
            id: validUserId, 
            user_id: validUserId,
            name: 'You',
            user_name: 'You',
            role: 'admin',
            is_online: true
          },
          { 
            id: companyId, 
            user_id: companyId,
            name: companyName,
            user_name: companyName,
            role: 'member',
            is_online: false
          }
        ];
      }
      
      const normalizedChatRoom = {
        ...newChatRoom,
        id: newChatRoom.id || newChatRoom.chat_id,
        room_name: newChatRoom.name || newChatRoom.room_name || companyName,
        room_type: 'direct',
        chat_type: newChatRoom.chat_type || 'one_to_one',
        participants: newChatRoom.participants,
        created_at: newChatRoom.created_at || new Date().toISOString(),
        last_message: null,
        unread_count: 0,
        metadata: {
          ...newChatRoom.metadata,
          companyId,
          companyName
        }
      };
      
      // 🔥🔥🔥 [수정 1] 새 채팅방을 chatRooms 배열에도 추가
      set(state => {
        const currentRooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        const exists = currentRooms.some(room => room && room.id === normalizedChatRoom.id);
        
        return {
          selectedChatRoom: normalizedChatRoom,
          // 🔥 chatRooms에 새 채팅방 추가 (존재하지 않는 경우에만)
          chatRooms: exists ? currentRooms : [normalizedChatRoom, ...currentRooms],
          isLoading: false,
          error: null,
        };
      });
      
      if (sendWelcomeMessage) {
        try {
          const welcomeText = welcomeMessageText || `안녕하세요, ${companyName}님과의 대화를 시작합니다.`;
          logger.debug('📩 환영 메시지 전송:', welcomeText);
          
          await get().sendMessage(normalizedChatRoom.id, {
            content: welcomeText,
            message_type: 'system',
            metadata: { isWelcomeMessage: true }
          });
        } catch (msgError) {
          logger.warn('⚠️ 환영 메시지 전송 실패 (무시 가능):', msgError);
        }
      }
      
      return normalizedChatRoom;
      
    } catch (error) {
      logger.error('❌ 채팅방 처리 실패:', error);
      
      set({
        error: error.message || '채팅방 처리에 실패했습니다.',
        isLoading: false,
      });
      
      throw error;
    }
  },
  fetchMessages: async (chatRoomId, page = 1, refresh = false) => {
    const isLoadMore = page > 1;
    
    set({ 
      isLoadingMessages: !isLoadMore,
      isLoadingMore: isLoadMore,
      error: null 
    });
    try {
      logger.debug(`📬 메시지 조회: 채팅방 ${chatRoomId}, 페이지 ${page}`);
      const response = await chatsService.getChatMessages(chatRoomId, {
        page,
        limit: 50,
      });
      let messagesData = [];
      if (response) {
        if (Array.isArray(response)) {
          messagesData = response;
        } else if (response.messages && Array.isArray(response.messages)) {
          messagesData = response.messages;
        } else if (response.data) {
          if (Array.isArray(response.data)) {
            messagesData = response.data;
          } else if (response.data.messages && Array.isArray(response.data.messages)) {
            messagesData = response.data.messages;
          }
        }
      }
      // 🔥 메시지별 읽음 상태 추가
      const currentUserId = get().currentUserId;
      const processedMessages = messagesData.map(msg => ({
        ...msg,
        is_read: msg.sender_id === currentUserId ? true : (msg.is_read ?? false)
      }));
      if (get().autoTranslateEnabled && get().preferredLanguage !== 'ko') {
        const translationPromises = processedMessages
          .filter(msg => msg.type === 'text' && !msg.isTranslated)
          .map(msg => get().translateMessage(msg.id, get().preferredLanguage, chatRoomId));
        
        Promise.all(translationPromises).catch(console.error);
      }
      logger.debug(`✅ 메시지 ${processedMessages.length}개 조회 완료`);
      set(state => {
        const existingMessages = state.messages[chatRoomId] || [];
        const newMessages = page === 1 ? processedMessages : [...processedMessages, ...existingMessages];
        
        return {
          messages: {
            ...state.messages,
            [chatRoomId]: newMessages,
          },
          messagePages: {
            ...state.messagePages,
            [chatRoomId]: page,
          },
          hasMoreMessages: {
            ...state.hasMoreMessages,
            [chatRoomId]: response?.hasMore || response?.has_more || false,
          },
          isLoadingMessages: false,
          isLoadingMore: false,
          error: null,
        };
      });
      return processedMessages;
    } catch (error) {
      logger.error('❌ 메시지 조회 실패:', error);
      set({
        error: error.message || '메시지 조회 실패',
        isLoadingMessages: false,
        isLoadingMore: false,
      });
      return [];
    }
  },
  // =================== 🔥🔥🔥 메시지 전송 (Optimistic Update) - 버그 수정 ===================
  sendMessage: async (chatRoomId, messageData) => {
    set({ isSending: true, error: null });
    try {
      let processedMessageData = { ...messageData };
      
      if (messageData.type === 'schedule' || messageData.type === 'event_schedule') {
        processedMessageData.isSchedule = true;
      }
      
      processedMessageData.language = get().preferredLanguage;
      
      logger.debug(`📤 메시지 전송 중: ${chatRoomId}`);
      
      const sentMessage = await chatsService.sendMessage(chatRoomId, processedMessageData);
      
      if (!sentMessage) {
        throw new Error('메시지 전송 응답이 없습니다.');
      }
      
      if (!sentMessage.id) {
        logger.error('❌ 응답에 메시지 ID가 없음:', sentMessage);
        throw new Error('메시지 ID가 없습니다.');
      }
      
      logger.debug('✅ 메시지 전송 완료:', {
        id: sentMessage.id,
        chat_id: sentMessage.chat_id || sentMessage.chatRoomId || sentMessage.conversation_id,
        content: sentMessage.content?.substring(0, 50),
        status: sentMessage.status
      });
      // 🔥 Optimistic Update: API 성공 시 즉시 화면에 표시
      logger.debug('✅ API 전송 성공, 로컬 상태 즉시 업데이트 (Optimistic Update):', sentMessage.id);
      get().handleNewMessage(sentMessage);
      const chatRoom = get().chatRooms.find(room => room.id === chatRoomId);
      if (chatRoom?.room_type === 'event' && chatRoom?.metadata?.supported_languages) {
        const supportedLangs = chatRoom.metadata.supported_languages;
        for (const lang of supportedLangs) {
          if (lang !== get().preferredLanguage) {
            get().translateMessage(sentMessage.id, lang, chatRoomId);
          }
        }
      }
      
      // 🔥🔥🔥 [핵심 수정] 미리보기 텍스트 생성 로직 추가
      const currentUserId = get().currentUserId;
      const msgType = sentMessage.message_type || sentMessage.type || 'text';
      const metadata = sentMessage.metadata || sentMessage.message_metadata || {};
      
      // 내가 보낸 메시지이므로 isSentByMe = true
      const isSentByMe = true;
      
      // 🔥 미리보기 텍스트 결정 (generateMeetingPreviewText 헬퍼 함수 사용)
      let previewContent = sentMessage.content;
      
      if (MEETING_MESSAGE_TYPES.includes(msgType)) {
        previewContent = generateMeetingPreviewText(msgType, isSentByMe, metadata);
      } else if (msgType === 'image') {
        previewContent = '📷 사진';
      } else if (msgType === 'file') {
        previewContent = '📎 파일';
      } else if (!previewContent || previewContent.trim() === '') {
        previewContent = '새 메시지';
      }
      
      logger.debug(`📊 [sendMessage] msgType=${msgType}, previewContent="${previewContent}"`);
      
      set(state => {
        const rooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        
        const chatRoomExists = rooms.some(room => room && room.id === chatRoomId);
        
        let updatedRooms = rooms;
        if (!chatRoomExists && state.selectedChatRoom?.id === chatRoomId) {
          logger.debug('✅ 첫 메시지 전송: 채팅방을 목록에 추가합니다.');
          updatedRooms = [
            {
              ...state.selectedChatRoom,
              lastMessage: sentMessage,
              last_message: previewContent,
              last_activity: sentMessage.created_at || sentMessage.createdAt || sentMessage.sentAt,
            },
            ...rooms
          ];
        } else {
          updatedRooms = rooms.map(room => room && room.id === chatRoomId
            ? { 
                ...room, 
                lastMessage: sentMessage,
                last_message: previewContent,
                last_activity: sentMessage.created_at || sentMessage.createdAt || sentMessage.sentAt,
              }
            : room
          );
        }
        
        return {
          chatRooms: updatedRooms
            .sort((a, b) => {
              if (a?.room_type === 'event' && b?.room_type !== 'event') return -1;
              if (a?.room_type !== 'event' && b?.room_type === 'event') return 1;
              if (a?.isPinned && !b?.isPinned) return -1;
              if (!a?.isPinned && b?.isPinned) return 1;
              
              const dateA = new Date(a?.last_activity || a?.updated_at || a?.created_at || 0);
              const dateB = new Date(b?.last_activity || b?.updated_at || b?.created_at || 0);
              return dateB - dateA;
            }),
          isSending: false,
          error: null,
        };
      });
      
      return sentMessage;
      
    } catch (error) {
      logger.error('❌ 메시지 전송 실패:', error);
      
      set({
        error: error.message || '메시지 전송 실패',
        isSending: false,
      });
      
      throw error;
    }
  },
  sendSystemMessage: async (chatRoomId, content, language = 'ko', messageType = 'system') => {
    try {
      const systemMessage = {
        type: messageType,
        content,
        language,
        isSystem: true,
        metadata: {
          timestamp: new Date().toISOString(),
          priority: messageType === 'broadcast' ? 'high' : 'normal'
        }
      };
      return await get().sendMessage(chatRoomId, systemMessage);
    } catch (error) {
      logger.error('❌ 시스템 메시지 전송 실패:', error);
      throw error;
    }
  },
  sendFileMessage: async (chatRoomId, fileData) => {
    set({ isSending: true, error: null });
    try {
      logger.debug(`📎 파일 메시지 전송 중: ${chatRoomId}`);
      const message = await chatsService.sendFileMessage(chatRoomId, fileData.file);
      logger.debug('✅ 파일 메시지 전송 완료');
      
      get().handleNewMessage(message);
      
      set(state => {
        const rooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        
        const chatRoomExists = rooms.some(room => room && room.id === chatRoomId);
        
        let updatedRooms = rooms;
        if (!chatRoomExists && state.selectedChatRoom?.id === chatRoomId) {
          logger.debug('✅ 첫 파일 메시지 전송: 채팅방을 목록에 추가합니다.');
          updatedRooms = [
            {
              ...state.selectedChatRoom,
              lastMessage: message,
              last_message: '📎 파일',
              last_activity: message.created_at || message.createdAt || message.sentAt,
            },
            ...rooms
          ];
        } else {
          updatedRooms = rooms.map(room => room && room.id === chatRoomId
            ? { 
                ...room, 
                lastMessage: message,
                last_message: '📎 파일',
                last_activity: message.created_at || message.createdAt || message.sentAt,
              }
            : room
          );
        }
        
        return {
          chatRooms: updatedRooms
            .sort((a, b) => {
              if (a?.room_type === 'event' && b?.room_type !== 'event') return -1;
              if (a?.room_type !== 'event' && b?.room_type === 'event') return 1;
              if (a?.isPinned && !b?.isPinned) return -1;
              if (!a?.isPinned && b?.isPinned) return 1;
              
              const dateA = new Date(a?.last_activity || a?.updated_at || a?.created_at || 0);
              const dateB = new Date(b?.last_activity || b?.updated_at || b?.created_at || 0);
              return dateB - dateA;
            }),
          isSending: false,
          error: null,
        };
      });
      return message;
    } catch (error) {
      logger.error('❌ 파일 메시지 전송 실패:', error);
      set({
        error: error.message || '파일 전송 실패',
        isSending: false,
      });
      throw error;
    }
  },
  // =================== 🔥 메시지 읽음 처리 (강화) ===================
  
  markAsRead: async (chatRoomId, messageId) => {
    try {
      const currentUserId = get().currentUserId;
      
      // 백엔드 API 호출
      await chatsService.markAsRead(chatRoomId, messageId);
      logger.debug(`✅ 메시지 읽음 처리: ${chatRoomId}/${messageId}`);
      
      // unread_count를 0으로 업데이트
      get().updateUnreadCount(chatRoomId, 0);
      
      // 🔥 메시지별 읽음 상태 업데이트
      set(state => {
        const chatMessages = state.messages[chatRoomId] || [];
        const updatedMessages = {
          ...state.messages,
          [chatRoomId]: chatMessages.map(msg => 
            // 내가 보낸 메시지가 아니고, 아직 읽지 않은 메시지만 읽음 처리
            msg.sender_id !== currentUserId && !msg.is_read
              ? { ...msg, is_read: true, status: 'read' }
              : msg
          )
        };
        
        return {
          messages: updatedMessages,
          lastReadMessageIds: {
            ...state.lastReadMessageIds,
            [chatRoomId]: messageId,
          }
        };
      });
    } catch (error) {
      logger.error('❌ 메시지 읽음 처리 실패:', error);
    }
  },
  createChatRoom: async (participants, type = 'direct', roomName = null) => {
    set({ isLoading: true, error: null });
    try {
      const chatRoomData = {
        participant_ids: participants,
        room_type: type,
      };
      
      if (roomName) {
        chatRoomData.room_name = roomName;
      }
      
      if (type === 'group' || type === 'breakout') {
        chatRoomData.max_participants = 500;
        chatRoomData.metadata = {
          supportLargeGroup: true,
          autoTranslate: true
        };
      }
      
      logger.debug('🔨 채팅방 생성 중:', chatRoomData);
      const chatRoom = await chatsService.createChatRoom(chatRoomData);
      logger.debug('✅ 채팅방 생성 완료:', chatRoom.id);
      
      set(state => {
        const currentRooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        return {
          chatRooms: [chatRoom, ...currentRooms],
          selectedChatRoom: chatRoom,
          isLoading: false,
          error: null,
        };
      });
      return chatRoom;
    } catch (error) {
      logger.error('❌ 채팅방 생성 실패:', error);
      set({
        error: error.message || '채팅방 생성 실패',
        isLoading: false,
      });
      throw error;
    }
  },
  joinChatRoom: async (chatRoomId) => {
    try {
      const limit = get().participantLimit[chatRoomId];
      if (limit) {
        const participants = await chatsService.getChatParticipants(chatRoomId);
        if (participants.length >= limit) {
          throw new Error(`이 채팅방은 최대 ${limit}명까지 참여 가능합니다.`);
        }
      }
      
      logger.debug(`🚪 채팅방 입장: ${chatRoomId}`);
      await chatsService.joinChatRoom(chatRoomId);
      logger.debug('✅ 채팅방 입장 완료');
      
      set(state => {
        const rooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        return {
          chatRooms: rooms.map(room =>
            room && room.id === chatRoomId
              ? { 
                  ...room, 
                  joined: true,
                  participantCount: (room.participantCount || 0) + 1
                }
              : room
          ),
        };
      });
      
      const preferredLang = get().preferredLanguage;
      if (preferredLang !== 'ko') {
        get().translateChatRoom(chatRoomId, preferredLang);
      }
      
    } catch (error) {
      logger.error('❌ 채팅방 입장 실패:', error);
      set({ error: error.message || '채팅방 입장 실패' });
      throw error;
    }
  },
  leaveChatRoom: async (chatRoomId) => {
    try {
      logger.debug(`🚪 채팅방 퇴장: ${chatRoomId}`);
      await chatsService.leaveChat(chatRoomId);
      logger.debug('✅ 채팅방 퇴장 완료');
      
      set(state => {
        const rooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        return {
          chatRooms: rooms.filter(room => room && room.id !== chatRoomId),
          selectedChatRoom: state.selectedChatRoom?.id === chatRoomId 
            ? null 
            : state.selectedChatRoom,
          messages: {
            ...state.messages,
            [chatRoomId]: undefined,
          },
          eventSchedules: {
            ...state.eventSchedules,
            [chatRoomId]: undefined,
          },
        };
      });
    } catch (error) {
      logger.error('❌ 채팅방 퇴장 실패:', error);
      set({ error: error.message || '채팅방 퇴장 실패' });
      throw error;
    }
  },
  deleteChat: async (chatId) => {
    try {
      logger.debug(`🗑️ 채팅방 삭제: ${chatId}`);
      await chatsService.deleteChat(chatId);
      logger.debug('✅ 채팅방 삭제 완료');
      
      set(state => {
        const rooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        return {
          chatRooms: rooms.filter(room => room && room.id !== chatId),
          selectedChatRoom: state.selectedChatRoom?.id === chatId 
            ? null 
            : state.selectedChatRoom,
          messages: {
            ...state.messages,
            [chatId]: undefined,
          },
          unreadCounts: {
            ...state.unreadCounts,
            [chatId]: undefined,
          },
          eventSchedules: {
            ...state.eventSchedules,
            [chatId]: undefined,
          },
        };
      });
    } catch (error) {
      logger.error('❌ 채팅방 삭제 실패:', error);
      set({ error: error.message || '채팅방 삭제 실패' });
      throw error;
    }
  },
  toggleChatPin: async (chatId) => {
    const chat = get().chatRooms.find(c => c && c.id === chatId);
    if (!chat) {
      logger.warn('채팅방을 찾을 수 없음:', chatId);
      return;
    }
    
    try {
      const newPinState = !chat.isPinned;
      logger.debug(`📌 채팅방 ${newPinState ? '고정' : '고정해제'}: ${chatId}`);
      await chatsService.pinChatRoom(chatId, newPinState);
      logger.debug('✅ 고정 상태 변경 완료');
      
      set(state => {
        const rooms = Array.isArray(state.chatRooms) ? state.chatRooms : [];
        return {
          chatRooms: rooms
            .map(room => room && room.id === chatId
              ? { ...room, isPinned: newPinState }
              : room
            )
            .sort((a, b) => {
              if (a?.room_type === 'event' && b?.room_type !== 'event') return -1;
              if (a?.room_type !== 'event' && b?.room_type === 'event') return 1;
              if (a?.isPinned && !b?.isPinned) return -1;
              if (!a?.isPinned && b?.isPinned) return 1;
              
              const dateA = new Date(a?.last_activity || a?.updated_at || a?.created_at || 0);
              const dateB = new Date(b?.last_activity || b?.updated_at || b?.created_at || 0);
              return dateB - dateA;
            }),
        };
      });
    } catch (error) {
      logger.error('❌ 고정 상태 변경 실패:', error);
      set({ error: error.message || '고정 상태 변경 실패' });
      throw error;
    }
  },
  fetchChats: async () => {
    return get().fetchChatRooms();
  },
  markChatAsRead: async (chatId) => {
    try {
      await chatsService.markAsRead(chatId);
      
      get().updateUnreadCount(chatId, 0);
      
      set(state => {
        const chatMessages = state.messages[chatId] || [];
        const currentUserId = state.currentUserId;
        
        const updatedMessages = {
          ...state.messages,
          [chatId]: chatMessages.map(msg => 
            msg.sender_id !== currentUserId && msg.status !== 'read' 
              ? { ...msg, status: 'read', is_read: true } 
              : msg
          )
        };
        
        return { messages: updatedMessages };
      });
    } catch (error) {
      logger.error('❌ 메시지 읽음 처리 실패:', error);
    }
  },
  // =================== 🔥🔥🔥 실시간 이벤트 처리 (수정됨) ===================
  
  /**
   * 🔥🔥🔥 새 메시지 처리 - 중복 방지 + 안전한 데이터 정규화 + 필드 동기화 + 현재 보고 있는 방 즉시 읽음 처리
   * 🔥🔥🔥 2025-12-19 수정: 미팅 메시지 미리보기에서 보낸사람/받는사람 구분
   */
  handleNewMessage: (message) => {
    logger.debug('📨 Store: 새 메시지 처리:', message);
    
    const { selectedChatRoom, currentUserId } = get();
    
    const chatRoomId = message.chat_id || message.chatRoomId || message.conversation_id;
    
    if (!chatRoomId) {
      logger.warn('⚠️ 메시지에 채팅방 ID가 없습니다:', message);
      return;
    }
    
    const isViewingThisRoom = selectedChatRoom?.id && String(selectedChatRoom.id) === String(chatRoomId);
    // 🔥🔥🔥 [핵심 수정] last_message 미리보기 텍스트 생성 (보낸사람/받는사람 구분)
    let previewContent = message.content;
    const msgType = message.message_type || message.type;
    const senderId = message.sender_id;
    const metadata = message.metadata || message.message_metadata || {};
    const existingChatRoom = get().chatRooms.find((chat) => String(chat.id) === String(chatRoomId));
    
    // 🔥🔥🔥 [핵심] 내가 보낸 메시지인지 확인
    const isSentByMe = currentUserId && String(senderId) === String(currentUserId);
    
    // content가 비어있거나 미팅 관련 메시지인 경우 타입에 따라 미리보기 텍스트 생성
    if (!previewContent || MEETING_MESSAGE_TYPES.includes(msgType)) {
      if (MEETING_MESSAGE_TYPES.includes(msgType)) {
        previewContent = generateMeetingPreviewText(msgType, isSentByMe, metadata);
      } else if (msgType === 'image') {
        previewContent = '📷 사진';
      } else if (msgType === 'file') {
        previewContent = '📎 파일';
      } else if (!previewContent) {
        previewContent = '새 메시지가 도착했습니다.';
      }
    }
    set(state => {
      const currentMessages = state.messages[chatRoomId] || [];
      const isDuplicate = currentMessages.some(m => m.id === message.id);
      
      let updatedMessages;
      
      if (isDuplicate) {
        updatedMessages = {
          ...state.messages,
          [chatRoomId]: currentMessages.map(m => m.id === message.id ? message : m)
        };
      } else {
        const messageToAdd = isViewingThisRoom 
          ? { ...message, is_read: true, status: 'read' } 
          : message;
        updatedMessages = {
          ...state.messages,
          [chatRoomId]: [...currentMessages, messageToAdd]
        };
      }
      
      const shouldIncrement = (!isDuplicate && String(message.sender_id) !== String(currentUserId) && !isViewingThisRoom);
      
      // 🔥🔥🔥 [핵심 수정] chatRooms 업데이트 - last_message와 lastMessage 동기화 강화
      const updatedChats = state.chatRooms.map(chat => {
        if (String(chat.id) === String(chatRoomId)) {
          const currentCount = parseInt(chat.unread_count || chat.unreadCount || 0, 10);
          const newCount = isViewingThisRoom ? 0 : (shouldIncrement ? currentCount + 1 : currentCount);
          
          // 🔥🔥🔥 [핵심] 메시지 전체 객체와 미리보기 문자열 모두 저장
          const lastMessageObject = {
            id: message.id,
            content: message.content || previewContent,
            sender_id: message.sender_id,
            sender_name: message.sender_name,
            message_type: msgType,
            created_at: message.created_at || message.sent_at || new Date().toISOString(),
            metadata: message.metadata || message.message_metadata,
          };
          
          return {
            ...chat,
            // 🔥🔥🔥 [중요] 두 필드 모두 업데이트하여 일관성 유지
            last_message: previewContent,  // 문자열 (채팅 목록 미리보기용)
            lastMessage: lastMessageObject, // 객체 (상세 정보용)
            last_activity: message.created_at || message.sent_at || new Date().toISOString(),
            unread_count: newCount,
            unreadCount: newCount, 
            has_unread: newCount > 0
          };
        }
        return chat;
      });

      if (!existingChatRoom) {
        const newCount = isViewingThisRoom ? 0 : (shouldIncrement ? 1 : 0);
        updatedChats.unshift({
          id: chatRoomId,
          chat_id: chatRoomId,
          room_type: message.room_type || message.chat_type || 'direct',
          chat_type: message.chat_type || message.room_type || 'direct',
          last_message: previewContent,
          lastMessage: {
            id: message.id,
            content: message.content || previewContent,
            sender_id: message.sender_id,
            sender_name: message.sender_name,
            message_type: msgType,
            created_at: message.created_at || message.sent_at || new Date().toISOString(),
            metadata: message.metadata || message.message_metadata,
          },
          last_activity: message.created_at || message.sent_at || new Date().toISOString(),
          unread_count: newCount,
          unreadCount: newCount,
          has_unread: newCount > 0,
          participants: [],
        });
      }
      
      // 🔥 최신 활동 순으로 정렬
      updatedChats.sort((a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0));
      
      logger.debug(`📊 [handleNewMessage] chatId=${chatRoomId}, preview="${previewContent}", isDuplicate=${isDuplicate}, isSentByMe=${isSentByMe}`);
      
      return {
        messages: updatedMessages,
        chatRooms: updatedChats,
        unreadCounts: {
          ...state.unreadCounts,
          [chatRoomId]: updatedChats.find(c => String(c.id) === String(chatRoomId))?.unread_count || 0
        }
      };
    });
    if (isViewingThisRoom && String(message.sender_id) !== String(currentUserId)) {
      get().markAsRead(chatRoomId, message.id);
    }
    if (!existingChatRoom) {
      setTimeout(() => {
        get().fetchChatRooms(true);
      }, 0);
    }
  },
  /**
   * 🔥🔥🔥 [추가] 미팅 상태 업데이트 처리 - 수락/거절 시 실시간 동기화
   */
  handleMeetingStatusUpdate: (data) => {
    const { meeting_id, message_id, chat_id, new_status, updated_by } = data;
    
    logger.debug('📅 [handleMeetingStatusUpdate] 미팅 상태 업데이트:', {
      meeting_id,
      message_id,
      chat_id,
      new_status,
      updated_by
    });
    
    if (!message_id && !meeting_id) {
      logger.warn('⚠️ message_id 또는 meeting_id가 없습니다');
      return;
    }
    
    // 🔥 UUID 정규화 함수
    const normalizeId = (id) => id ? String(id).trim().toLowerCase() : null;
    
    const normalizedMessageId = normalizeId(message_id);
    const normalizedMeetingId = normalizeId(meeting_id);
    const normalizedChatId = normalizeId(chat_id);
    
    set(state => {
      const updatedMessages = { ...state.messages };
      let updateCount = 0;
      
      // 특정 채팅방 또는 모든 채팅방에서 검색
      const chatIdsToSearch = normalizedChatId && updatedMessages[chat_id] 
        ? [chat_id] 
        : Object.keys(updatedMessages);
      
      chatIdsToSearch.forEach(roomId => {
        if (!updatedMessages[roomId]) return;
        
        updatedMessages[roomId] = updatedMessages[roomId].map(msg => {
          // 🔥 metadata 필드 통합 (metadata 또는 message_metadata)
          const msgMetadata = msg.metadata || msg.message_metadata || {};
          const msgMeetingId = normalizeId(msgMetadata.meeting_id || msgMetadata.meeting?.id);
          const msgId = normalizeId(msg.id);
          
          // 🔥 UUID 정규화 비교
          const isTargetMessage = 
            (normalizedMessageId && msgId === normalizedMessageId) ||
            (normalizedMeetingId && msgMeetingId === normalizedMeetingId);
          
          if (isTargetMessage) {
            updateCount++;
            logger.debug(`✅ 메시지 상태 업데이트: ${msg.id} → ${new_status}`);
            
            return {
              ...msg,
              metadata: {
                ...msgMetadata,
                invitation_status: new_status,
                proposal_status: msgMetadata.proposal_status ? new_status : msgMetadata.proposal_status,
                updated_at: new Date().toISOString(),
                updated_by: updated_by
              },
              // 🔥 message_metadata도 동기화 (일부 컴포넌트에서 사용할 수 있음)
              message_metadata: {
                ...msgMetadata,
                invitation_status: new_status,
                proposal_status: msgMetadata.proposal_status ? new_status : msgMetadata.proposal_status,
                updated_at: new Date().toISOString(),
                updated_by: updated_by
              }
            };
          }
          return msg;
        });
      });
      
      if (updateCount === 0) {
        logger.warn('⚠️ 업데이트할 메시지를 찾지 못함:', { normalizedMessageId, normalizedMeetingId });
      } else {
        logger.debug(`✅ 총 ${updateCount}개 메시지 업데이트 완료`);
      }
      
      return { messages: updatedMessages };
    });
  },
  handleReadReceiptUpdate: (data) => {
    const {
      chat_id: chatId,
      reader_user_id: readerUserId,
      read_message_ids: readMessageIds = [],
      read_at: readAt,
      last_read_at: lastReadAt,
    } = data || {};

    if (!chatId || !readerUserId) {
      return;
    }

    const normalizedChatId = String(chatId);
    const normalizedReaderId = String(readerUserId);
    const readMessageIdSet = new Set((readMessageIds || []).map((id) => String(id)));
    const effectiveReadAt = readAt || lastReadAt || new Date().toISOString();

    set(state => {
      const updatedMessages = { ...state.messages };
      const roomMessages = updatedMessages[normalizedChatId] || updatedMessages[chatId];

      if (Array.isArray(roomMessages)) {
        updatedMessages[normalizedChatId] = roomMessages.map((msg) => {
          const shouldMarkRead =
            (readMessageIdSet.size === 0 || readMessageIdSet.has(String(msg.id))) &&
            String(msg.sender_id) !== normalizedReaderId;

          if (!shouldMarkRead) {
            return msg;
          }

          const existingReadBy = Array.isArray(msg.read_by)
            ? msg.read_by
            : (Array.isArray(msg.readBy) ? msg.readBy : []);
          const alreadyRead = existingReadBy.some((reader) => {
            const readerId = reader?.user_id || reader?.userId || reader?.id;
            return readerId && String(readerId) === normalizedReaderId;
          });

          const nextReadBy = alreadyRead
            ? existingReadBy
            : [...existingReadBy, { user_id: normalizedReaderId, read_at: effectiveReadAt }];

          return {
            ...msg,
            read_by: nextReadBy,
            readBy: nextReadBy,
            status: 'read',
            is_read: true,
          };
        });
      }

      const updatedRooms = state.chatRooms.map((room) => {
        if (String(room.id) !== normalizedChatId) {
          return room;
        }

        const participants = (room.participants || []).map((participant) => {
          const participantId = participant.user_id || participant.userId || participant.id;
          if (!participantId || String(participantId) !== normalizedReaderId) {
            return participant;
          }

          return {
            ...participant,
            last_read_at: lastReadAt || effectiveReadAt,
            lastReadAt: lastReadAt || effectiveReadAt,
          };
        });

        return {
          ...room,
          participants,
        };
      });

      return {
        messages: updatedMessages,
        chatRooms: updatedRooms,
      };
    });
  },
  /**
   * 🔥 타이핑 상태 처리 - Set에서 배열로 변경 + ID 문자열 정규화 추가
   */
  handleTyping: (chatRoomId, userId, isTyping) => {
    const { currentUserId } = get();
    
    // 🔥 ID 정규화 (문자열 비교를 위해)
    const userIdStr = String(userId);
    const currentUserIdStr = String(currentUserId);
    // 내 타이핑은 무시 (로컬 state로 처리하거나 무시)
    if (userIdStr === currentUserIdStr) {
      logger.debug('⏭️ 내 타이핑 이벤트 무시:', userId);
      return;
    }
    
    logger.debug(`⌨️ 타이핑 상태 업데이트: chatId=${chatRoomId}, userId=${userIdStr}, isTyping=${isTyping}`);
    
    set(state => {
      // 🔥 Set이 아닌 배열로 관리하여 React 리렌더링 보장
      const currentTyping = state.typingUsers[chatRoomId] || [];
      
      let newTyping;
      
      if (isTyping) {
        // 이미 있으면 추가하지 않음 (문자열 비교)
        if (!currentTyping.includes(userIdStr)) {
          newTyping = [...currentTyping, userIdStr];
          logger.debug(`✅ 타이핑 사용자 추가: ${userIdStr}`);
        } else {
          newTyping = currentTyping;
        }
      } else {
        // 제거 (문자열 비교)
        newTyping = currentTyping.filter(id => String(id) !== userIdStr);
        logger.debug(`❌ 타이핑 사용자 제거: ${userIdStr}`);
      }
      
      logger.debug(`📊 타이핑 중인 사용자 (${chatRoomId}):`, newTyping);
      
      return {
        typingUsers: {
          ...state.typingUsers,
          [chatRoomId]: newTyping
        }
      };
    });
  },

  /**
   * 🔥🔥🔥 [추가] 상대방 채팅방 퇴장 처리
   * - 채팅방 상태 업데이트 (상대방 나감 표시)
   * - 시스템 메시지 추가
   * - 토스트 알림 (선택적)
   */
  handleUserLeftChat: (data) => {
    const { chatId, userId, userName, message, timestamp } = data;
    
    logger.debug('🚪 [handleUserLeftChat] 상대방 채팅방 퇴장:', {
      chatId,
      userId,
      userName,
      message
    });
    
    if (!chatId) {
      logger.warn('⚠️ handleUserLeftChat: chatId가 없습니다');
      return;
    }
    
    set(state => {
      // 1. 채팅방 정보 업데이트 - 상대방이 나갔음을 표시
      const updatedChatRooms = state.chatRooms.map(room => {
        if (room.id === chatId) {
          // 참가자 목록에서 해당 사용자를 비활성화
          const updatedParticipants = (room.participants || []).map(p => {
            const participantId = p.user_id || p.id;
            if (String(participantId) === String(userId)) {
              return {
                ...p,
                is_active: false,
                left_at: timestamp || new Date().toISOString()
              };
            }
            return p;
          });
          
          return {
            ...room,
            participants: updatedParticipants,
            // 채팅방 메타데이터에 퇴장 정보 추가
            metadata: {
              ...(room.metadata || {}),
              partner_left: true,
              partner_left_at: timestamp || new Date().toISOString(),
              partner_left_name: userName
            },
            // 마지막 메시지 업데이트
            last_message: message || `${userName}님이 채팅방을 나갔습니다.`,
            last_activity: timestamp || new Date().toISOString()
          };
        }
        return room;
      });
      
      // 2. 현재 선택된 채팅방이 해당 채팅방이면 selectedChatRoom도 업데이트
      let updatedSelectedChatRoom = state.selectedChatRoom;
      if (state.selectedChatRoom?.id === chatId) {
        const updatedRoom = updatedChatRooms.find(r => r.id === chatId);
        if (updatedRoom) {
          updatedSelectedChatRoom = updatedRoom;
        }
      }
      
      logger.debug('✅ [handleUserLeftChat] 채팅방 상태 업데이트 완료:', chatId);
      
      return {
        chatRooms: updatedChatRooms,
        selectedChatRoom: updatedSelectedChatRoom
      };
    });
    
    // 3. 🔥 브라우저 알림 이벤트 발송 (토스트 알림용)
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('userLeftChat', {
        detail: {
          chatId,
          userId,
          userName,
          message: message || `${userName}님이 채팅방을 나갔습니다.`,
          timestamp: timestamp || new Date().toISOString()
        }
      }));
      logger.debug('📢 [이벤트 발생] userLeftChat:', userName);
    }
  },

  // =================== 검색 ===================
  
  searchMessages: async (query, language = null) => {
    if (!query.trim()) {
      set({ searchQuery: '', searchResults: [] });
      return;
    }
    set({ isSearching: true, searchQuery: query });
    try {
      logger.debug(`🔍 메시지 검색: "${query}" (언어: ${language || 'all'})`);
      
      let searchQueries = [query];
      if (language) {
        const translatedQuery = await translationService.translate(query, language, 'auto');
        searchQueries.push(translatedQuery);
      }
      
      const allResults = [];
      for (const q of searchQueries) {
        const results = await chatsService.searchMessages(null, { query: q });
        allResults.push(...results);
      }
      
      const uniqueResults = Array.from(
        new Map(allResults.map(r => [r.id, r])).values()
      );
      
      logger.debug(`✅ 검색 완료: ${uniqueResults.length}개 결과`);
      
      set({
        searchResults: uniqueResults,
        isSearching: false,
      });
    } catch (error) {
      logger.error('❌ 메시지 검색 실패:', error);
      set({
        searchResults: [],
        error: error.message || '검색 실패',
        isSearching: false,
      });
    }
  },
  // =================== 유틸리티 메서드들 ===================
  
  // =================== 🔥 [수정] 채팅방 선택 (진입 시 뱃지 즉시 제거) ===================
  selectChatRoom: (chatRoom) => {
    // 1. 선택된 채팅방 설정
    set({ selectedChatRoom: chatRoom });
    
    if (chatRoom) {
      const chatId = chatRoom.id;
      // 2. 🔥 [핵심] UI 반응성을 위해 로컬 상태의 뱃지(unreadCount)를 즉시 0으로 초기화 (Optimistic Update)
      set(state => ({
        unreadCounts: {
          ...state.unreadCounts,
          [chatId]: 0
        },
        chatRooms: state.chatRooms.map(room => 
          room.id === chatId 
            ? { ...room, unread_count: 0, unreadCount: 0, has_unread: false }
            : room
        )
      }));
      // 3. 백엔드에 읽음 처리 요청 (비동기)
      // 가장 마지막 메시지가 있다면 그 메시지까지 읽은 것으로 처리
      const messages = get().messages[chatId];
      const lastMessage = messages?.[messages.length - 1];
      
      if (lastMessage) {
        get().markAsRead(chatId, lastMessage.id);
      } else {
        // 메시지가 없더라도 채팅방 자체를 읽음 처리
        get().markChatAsRead(chatId);
      }
      
      // 4. 번역 등 추가 로직 (기존 유지)
      if (get().autoTranslateEnabled && get().preferredLanguage !== 'ko') {
        get().translateChatRoom(chatId, get().preferredLanguage);
      }
    }
  },
  clearSelectedChatRoom: () => {
    set({ selectedChatRoom: null });
  },
  loadMoreMessages: async (chatRoomId) => {
    const { messagePages, hasMoreMessages } = get();
    const currentPage = messagePages[chatRoomId] || 1;
    
    if (hasMoreMessages[chatRoomId] !== false) {
      await get().fetchMessages(chatRoomId, currentPage + 1);
    }
  },
  clearError: () => {
    set({ error: null });
  },
  
  get chats() {
    const rooms = get().chatRooms;
    return Array.isArray(rooms) ? rooms : [];
  },
  /**
   * 🔥 타이핑 중인 사용자 조회 - 배열 반환
   */
  getTypingUsers: (chatRoomId) => {
    const { typingUsers } = get();
    const typing = typingUsers[chatRoomId];
    
    // 배열이면 그대로 반환, Set이면 배열로 변환 (하위 호환성)
    if (Array.isArray(typing)) {
      return typing;
    } else if (typing instanceof Set) {
      return Array.from(typing);
    }
    
    return [];
  },
  // =================== MICE 전용 유틸리티 ===================
  
  getEventChatRoom: (eventId) => {
    return get().eventChatRooms[eventId];
  },
  getEventSchedules: (chatRoomId) => {
    return get().eventSchedules[chatRoomId] || [];
  },
  getTranslatedMessage: (messageId, language) => {
    return get().translatedMessages[messageId]?.[language];
  },
  getSupportedLanguages: () => {
    return translationService.supportedLanguages;
  },
  getActiveEvents: () => {
    return get().activeEvents;
  },
  endEvent: async (eventId) => {
    const chatRoomId = get().eventChatRooms[eventId];
    if (!chatRoomId) return;
    try {
      await get().sendSystemMessage(
        chatRoomId,
        '행사가 종료되었습니다. 참여해주셔서 감사합니다! 🎊',
        'ko',
        'broadcast'
      );
      set(state => ({
        activeEvents: state.activeEvents.filter(e => e.eventId !== eventId),
        chatRooms: state.chatRooms.map(room =>
          room.id === chatRoomId
            ? { ...room, metadata: { ...room.metadata, status: 'ended' } }
            : room
        ),
      }));
      logger.debug(`✅ 행사 종료: ${eventId}`);
    } catch (error) {
      logger.error('❌ 행사 종료 실패:', error);
      throw error;
    }
  },
  refreshCompanies: async () => {
    return get().fetchChatRooms(true);
  },
}));
export { useChatsStore };
export default useChatsStore;
